import {useState} from 'react'

import PropTypes from 'prop-types'

import {Drawer, DRAWER_PLACEMENTS} from '@adv-mt/ui/drawer'

import CarfaxDrawer from './CarfaxDrawer/index.js'
import CertificationDrawer from './CertificationDrawer/index.js'
import DealerRatingsDrawer from './DealerRatingsDrawer/index.js'
import IncludedExtrasDrawer from './IncludedExtrasDrawer/index.js'
import InsuranceInfoDrawer from './InsuranceInfoDrawer/index.js'
import NoCommissionsDrawer from './NoCommissionsDrawer/index.js'
import PriceIndicatorDrawer from './PriceIndicatorDrawer/index.js'
import RentingServicesDrawer from './RentingServicesDrawer/index.js'
import ReportAdDrawer from './ReportAdDrawer/index.js'
import SellerServicesDrawer from './SellerServicesDrawer/index.js'
import TechnicalDetailsDrawer from './TechnicalDetailsDrawer/index.js'
import {DRAWER_TYPES} from './config.js'

const DRAWER_RENDERERS = {
  [DRAWER_TYPES.CERTIFICATION]: (data, onClose) => <CertificationDrawer onClose={onClose} features={data} />,
  [DRAWER_TYPES.NO_COMMISSIONS]: (data, onClose) => <NoCommissionsDrawer onClose={onClose} />,
  [DRAWER_TYPES.CARFAX]: (data, onClose) => <CarfaxDrawer onClose={onClose} reportUrl={data} />,
  [DRAWER_TYPES.PRICE_INDICATOR]: (data, onClose) => <PriceIndicatorDrawer onClose={onClose} ad={data} />,
  [DRAWER_TYPES.RENTING_SERVICES]: (data, onClose) => <RentingServicesDrawer onClose={onClose} services={data} />,
  [DRAWER_TYPES.INCLUDED_EXTRAS]: (data, onClose) => (
    <IncludedExtrasDrawer onClose={onClose} optionalEquipment={data} />
  ),
  [DRAWER_TYPES.SELLER_SERVICES]: (data, onClose) => (
    <SellerServicesDrawer hasShipping={data.hasShipping} onClose={onClose} services={data.services} />
  ),
  [DRAWER_TYPES.INSURANCE_INFO]: (data, onClose) => <InsuranceInfoDrawer insuranceId={data} onClose={onClose} />,
  [DRAWER_TYPES.TECHNICAL_DETAILS]: (data, onClose) => (
    <TechnicalDetailsDrawer
      onClose={onClose}
      groupedSpecifications={data.groupedSpecifications}
      standardEquipment={data.standardEquipment}
      optionalEquipment={data.optionalEquipment}
    />
  ),
  [DRAWER_TYPES.REPORT_AD]: (data, onClose) => (
    <ReportAdDrawer adId={data.adId} isProfessional={data.isProfessional} onClose={onClose} />
  ),
  [DRAWER_TYPES.DEALER_RATINGS]: (data, onClose) => (
    <DealerRatingsDrawer
      ad={data.ad}
      onClose={onClose}
      pageName={data.pageName}
      ratings={data.ratings}
      reviews={data.reviews}
    />
  )
}

// Mounted for the whole page, never conditionally: Base UI only emits `data-starting-style` when
// `open` changes after mount, so a root that mounts already open never plays the enter transition.
// `activeDrawer` goes null the moment the user closes, but the exit transition still needs the
// content, so the last drawer is kept until the DS reports the close as complete. That also
// unmounts it, so reopening remounts and re-fires whatever the drawer tracks on mount.
// A type with no renderer must leave this root CLOSED, not open with a null body: the dialog root
// registers its document-level dismiss listeners as soon as `open` is true, and with no Popup its
// `floatingElement` is null, so no click counts as inside and the backdrop containment check
// short-circuits to "outside". That turns the empty root into a global click-to-close that clears
// `activeDrawer` — which is exactly how a click inside the contact drawer, portalled from its own
// ContactForm instance, was closing it.
const hasRenderer = drawer => drawer !== null && DRAWER_RENDERERS[drawer.type] !== undefined

const DetailDrawer = ({activeDrawer = null, onClose}) => {
  const [renderedDrawer, setRenderedDrawer] = useState(activeDrawer)

  if (activeDrawer !== null && activeDrawer !== renderedDrawer) setRenderedDrawer(activeDrawer)

  return (
    <Drawer
      onOpenChange={open => !open && onClose()}
      onOpenChangeComplete={open => !open && setRenderedDrawer(null)}
      open={hasRenderer(activeDrawer)}
      placement={DRAWER_PLACEMENTS.RIGHT}
    >
      {renderedDrawer !== null && DRAWER_RENDERERS[renderedDrawer.type]?.(renderedDrawer.data, onClose)}
    </Drawer>
  )
}

DetailDrawer.displayName = 'DetailDrawer'

DetailDrawer.propTypes = {
  activeDrawer: PropTypes.shape({
    data: PropTypes.any,
    type: PropTypes.oneOf(Object.values(DRAWER_TYPES)).isRequired
  }),
  onClose: PropTypes.func.isRequired
}

export default DetailDrawer
